import { Injectable, Pipe, PipeTransform } from '@angular/core';
import moment from 'moment-timezone';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { Charge } from '../../models/entities/charge';

/**
 * The minimum a charge has to expose for us to decide whether it reads as forgiven.
 * Loose enough for the plain objects the unit invoices table works with.
 */
export interface ForgivableCharge
{
    forgiven?: boolean;
    paid?: boolean;
    deletedAt?: string;
    /**
     * Required, not optional: the amount still owed is the only thing that separates a real
     * write-off from a member who still owes the money, so a caller that cannot supply it cannot
     * answer this question. The server side of that contract is pinned by
     * UnitAccountHistoryController IndexTest::account_history_charge_line_carries_the_balance_and_forgiven_the_label_depends_on.
     */
    balance: number;
}

/**
 * Whether a charge should be LABELLED forgiven.
 *
 * `forgiven` is sticky on the charge record — nothing used to clear it once set — so an invoice
 * can carry the flag while it is live and still owed: a partially forgiven invoice whose payment
 * is later removed or returned, or (before SD-9820 fixed it) a reinstated one. Reading the flag on
 * its own then labelled an overdue invoice "Forgiven" while the member still owed the money
 * (SD-9820, org 44628, unit 37 Chip Shot Loop). A charge that is live and owed is not forgiven
 * whatever the flag says, so the flag only decides the label once the invoice is settled or gone.
 *
 * Shared by every surface that labels a charge so the admin and resident views cannot disagree.
 */
export function displaysAsForgiven(charge: ForgivableCharge): boolean {
    if (!charge.forgiven) {
        return false;
    }

    // `?? 0` is belt-and-braces for untyped callers (the invoices table works with plain objects):
    // the field is required above, and every payload that reaches these surfaces carries it.
    const liveAndOwed = !charge.deletedAt && !charge.paid && (charge.balance ?? 0) > 0;

    return !liveAndOwed;
}

@Pipe({
    name: 'chargeStatus',
    standalone: true,
})
@Injectable({
    providedIn: 'root',
})
export class ChargeStatusPipe implements PipeTransform
{
    public transform(charge: Charge) {
        if (displaysAsForgiven(charge)) {
            return 'forgiven';
        }

        if (charge.paid) {
            return 'paid';
        }

        if (charge.deletedAt) {
            return 'deleted';
        }

        const timezone = LegFiJwtService.getTimezone();
        const now = moment();
        const activeAfter = moment.utc(charge.activeAfter, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        const paymentDueOn = moment.utc(charge.paymentDueOn, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        if (paymentDueOn < now) {
            return 'overdue';
        } else if (activeAfter > now) {
            return 'future';
        } else if (charge.paymentsSum > 0) {
            return 'partial';
        } else {
            return 'current';
        }
    }
}
